// FP3-115B — Visual student-constraint overlay for the Schedule Builder.
//
// Renders subtle blue-slate hatched rectangles behind existing schedule
// blocks showing when the currently-selected student cannot be
// scheduled due to their own Service-Demographics Student Constraints
// (OT pull-out, counseling, intervention, testing, other blocked
// periods, etc.). Pairs with the FP3-115 Grade Constraint Overlay:
// the two use distinct visual treatments so a provider can tell at a
// glance which restriction they're looking at.
//
// This module is a pure resolver + pure component — no fetching, no
// state, no side-effects. The caller passes the already-fetched
// `studentConstraints` list (loaded once in ScheduleBuilder via
// `api.listStudentConstraints`, the SAME canonical source used by
// `findStudentConstraintConflict` for actual Builder drop-blocking
// enforcement) so overlay + validator are byte-identical.
//
// Contract (spec §12):
//   • pointer-events: none — never intercepts clicks / drag-drop
//   • zIndex: 0 + rendered before shared/placed blocks so both
//     paint on top
//   • never appears in Save payloads or unsaved-change state
//   • labels use student-facing copy ("Student · OT") — never
//     internal ids
import { pct, toMin } from "./utils";

/**
 * Return the flat list of student-constraint rects for the
 * currently-selected student, filtered by the currently-displayed
 * week and honouring FP3-107 canonical `days[]` (with legacy
 * `day_of_week` fallback).
 *
 * Params:
 *   • `studentConstraints` — full list from `api.listStudentConstraints()`.
 *   • `studentId` — the currently-selected student's id.
 *   • `weekStart` / `weekEnd` — ISO YYYY-MM-DD strings covering
 *     Mon..Fri of the currently-displayed Builder week. Applies the
 *     `start_date <= weekEnd && (end_date >= weekStart || end_date is
 *     blank)` window per spec §9.
 */
export function resolveStudentConstraintsForOverlay({
  studentConstraints,
  studentId,
  weekStart,
  weekEnd,
}) {
  if (!studentId) return [];
  if (!Array.isArray(studentConstraints) || studentConstraints.length === 0) {
    return [];
  }
  const out = [];
  studentConstraints.forEach((sc) => {
    if (sc.student_id !== studentId) return;
    // Date range applicability (spec §9). Blank endpoints = open.
    if (weekEnd && sc.start_date && sc.start_date > weekEnd) return;
    if (weekStart && sc.end_date && sc.end_date < weekStart) return;
    // FP3-107 canonical `days[]` with legacy fallback.
    const rawDays = Array.isArray(sc.days) && sc.days.length
      ? sc.days
      : typeof sc.day_of_week === "number"
      ? [sc.day_of_week]
      : [];
    const days = rawDays.filter(
      (d) => typeof d === "number" && d >= 0 && d <= 4,
    );
    if (days.length === 0) return;
    let sm;
    let em;
    try {
      sm = toMin(sc.start_time);
      em = toMin(sc.end_time);
    } catch {
      return;
    }
    if (!Number.isFinite(sm) || !Number.isFinite(em) || em <= sm) return;
    days.forEach((d) => {
      out.push({
        id: `${sc.id || sc._id || "sc"}-${d}`,
        constraintId: sc.id || sc._id || "sc",
        day: d,
        startMin: sm,
        endMin: em,
        kind: (sc.kind || "other").toString(),
        label: (sc.label || "").toString(),
      });
    });
  });
  return out;
}

function studentRuleLabel(rule) {
  const custom = (rule.label || "").trim();
  if (custom) return custom;
  const kind = (rule.kind || "").toString();
  const map = {
    ot: "OT",
    counseling: "Counseling",
    intervention: "Intervention",
    medical: "Medical",
    testing: "Testing",
    other: "Blocked",
  };
  return map[kind] || (kind
    ? kind.charAt(0).toUpperCase() + kind.slice(1)
    : "Blocked");
}

/**
 * Merges overlapping/adjacent rects of the same day into one
 * visual region. Same technique used by GradeConstraintOverlay so
 * two grade rules + one student rule on the same day don't turn
 * into an unreadable pile.
 */
function mergeOverlappingByDay(blocks) {
  const byDay = new Map();
  blocks.forEach((b) => {
    if (!byDay.has(b.day)) byDay.set(b.day, []);
    byDay.get(b.day).push(b);
  });
  const out = [];
  byDay.forEach((arr, day) => {
    const sorted = [...arr].sort((a, b) => a.startMin - b.startMin);
    let cur = null;
    sorted.forEach((b) => {
      if (!cur) {
        cur = {
          day,
          startMin: b.startMin,
          endMin: b.endMin,
          rules: [b],
        };
        return;
      }
      if (b.startMin <= cur.endMin) {
        cur.endMin = Math.max(cur.endMin, b.endMin);
        cur.rules.push(b);
      } else {
        out.push(cur);
        cur = {
          day,
          startMin: b.startMin,
          endMin: b.endMin,
          rules: [b],
        };
      }
    });
    if (cur) out.push(cur);
  });
  return out;
}

/**
 * Renders the student-constraint overlay rectangles for one day
 * row. Visual treatment is intentionally different from the grade
 * overlay's warm amber palette (§4): a cool blue-slate tint with a
 * 45-degree hatched fill so overlapping Grade + Student blocks
 * remain individually readable.
 */
export default function StudentConstraintOverlay({ blocks, dayIdx }) {
  const dayBlocks = (blocks || []).filter((b) => b.day === dayIdx);
  if (dayBlocks.length === 0) return null;
  const merged = mergeOverlappingByDay(dayBlocks);
  return (
    <>
      {merged.map((m) => {
        const left = pct(m.startMin);
        const width = pct(m.endMin) - pct(m.startMin);
        const primary = studentRuleLabel(m.rules[0]);
        const extra = m.rules.length > 1 ? ` +${m.rules.length - 1}` : "";
        const compactLabel = `Student · ${primary}${extra}`;
        const tooltipLines = m.rules.map(
          (r) =>
            `Student · ${studentRuleLabel(r)} · ` +
            `${String(Math.floor(r.startMin / 60)).padStart(2, "0")}:` +
            `${String(r.startMin % 60).padStart(2, "0")}–` +
            `${String(Math.floor(r.endMin / 60)).padStart(2, "0")}:` +
            `${String(r.endMin % 60).padStart(2, "0")}`,
        );
        return (
          <div
            key={`student-overlay-${dayIdx}-${m.rules[0].constraintId}-${m.startMin}`}
            data-testid={`student-overlay-block-${dayIdx}-${m.rules[0].constraintId}`}
            data-student-overlay="true"
            aria-hidden="true"
            className="absolute rounded overflow-hidden"
            style={{
              left: `${left}%`,
              width: `${Math.max(0.5, width)}%`,
              top: "4%",
              bottom: "4%",
              // Cool blue-slate — distinct from the amber grade
              // overlay (§4). Different hatch angle (45deg vs 135deg
              // on the grade overlay) so overlapping rules remain
              // individually readable.
              backgroundColor: "rgba(210, 218, 234, 0.30)",
              backgroundImage:
                "repeating-linear-gradient(45deg, transparent 0 6px, rgba(80, 100, 160, 0.14) 6px 7px)",
              border: "1px dashed rgba(105, 120, 175, 0.65)",
              pointerEvents: "none",
              zIndex: 0,
            }}
            title={tooltipLines.join("\n")}
          >
            <div className="px-1 py-0.5 text-[9px] text-[#3D4B7A] truncate italic leading-tight">
              {compactLabel}
            </div>
          </div>
        );
      })}
    </>
  );
}
